import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  Badge,
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Icon,
  PrimaryFullScreenSpinner,
  PrimarySparkLogo,
  type TanstackAppForm,
  toast,
  useAppForm,
} from "@repo/ui";
import { NotFoundError, neverFunc, prettyPrintEnum } from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { Suspense } from "react";
import {
  RiCheckboxBlankCircleFill,
  RiCheckboxCircleFill,
} from "react-icons/ri";
import { z } from "zod";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { brandLogosByKey } from "~/components/ProviderIcon";
import { graphql } from "~/gql/client";
import type {
  AdminFileImportTransactionFragment,
  CreateLedgerTransactionInput,
  CreateTransferInput,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

const maxDays = 14;

graphql(`
  fragment FileTransferImportOption on FileImportTransactionTransferOption {
    id
    transfer {
      id
      requestedForDate
      description
      shortRef
      currencyAmount {
        amount
        currency
      }
      sourceAsset {
          id
          businessEntity {
              id
              name
              organisation {
                  id
                  name
                  squareLogoUrl
              }
          }
          custodyAccount {
              id
          }
      }
      destinationAsset {
          id
          custodyAccount {
              id
          }
      }
    }
    matchedWithReference
  }

  fragment FileTransactionWestpacPaymentInstructionOption on FileImportTransactionWestpacPaymentInstructionOption {
    id
    westpacPaymentInstruction {
      id
      shortRef
      status
      currencyAmount {
        amount
        currency
      }
      destinationCustodyAccount {
        id
        shortRef
        product {
          id
          internalName
          provider {
              id
              name
          }
        }
      }
      transfers {
        id
        sourceAsset {
            id
        }
        destinationAsset {
            id
        }
        currencyAmount {
            currency
            amount
        }
      }
    }
  }

  fragment FileTransactionBlackrockUnitOrderOption on FileImportTransactionBlackrockUnitOrderOption {
    id
    blackrockUnitOrder {
      id
      shortRef
      currencyAmount {
        amount
        currency
      }
      custodyAccount {
          id
          shortRef
          product {
              id
              internalName
              name
              provider {
                  id
                  internalName
                  name
              }
          }
      }
      transfers {
        id
        sourceAsset {
            id
        }
        destinationAsset {
            id
        }
        currencyAmount {
            currency
            amount
        }
      }
    }
  }

  fragment FilePayoutImportOption on FileImportTransactionPayoutOption {
    id
    payout {
      id
      requestedForDate
      amount {
        amount
        currency
      }
      beneficiaryAccount {
        id
        accountName
      }
      shortRef
    }
    matchedWithReference
  }

  fragment FileTransferNewImportOption on FileImportTransactionTransferNewOption {
    id
    transferId
    asset {
      id
      name
    }
  }

  fragment FileDepositImportOption on FileImportTransactionNewDepositOption {
    id
  }

  fragment FileAnnotationImportOption on FileImportTransactionAnnotationOption {
    id
    annotation
  }

  fragment AdminFileImportTransaction on FileImportTransaction {
    id
    rowNumber
    importStatus
    description
    date
    amount
    custodyAccount {
        id
        currency
    }
    asset {
      id
      currency
      businessEntity {
        id
        name
        organisation {
          id
          name
          squareLogoUrl
        }
      }
      name
    }
    ledgerEntry {
      id
      shortRef
      transaction {
        id
      }
    }
    options {
      ... FileTransferNewImportOption
      ... FileDepositImportOption
      ... FilePayoutImportOption
      ... FileAnnotationImportOption
      ... FileTransferImportOption
      ... FileTransactionWestpacPaymentInstructionOption
      ... FileTransactionBlackrockUnitOrderOption
    }
  }

  fragment AdminMacquarieFileImportFileDetails on MacquarieTransactionImportFile {
    id
    shortRef
    fileName
    uploadedAt
    createdByUser {
      id
      ...UserProfileUI
    }
    reconciliation(maxDays: $maxDays) {
      id
      maxDays
      totalTransactionCount
      transactions {
        id
        ...AdminFileImportTransaction
      }
    }
  }

  fragment AdminWestpacFileImportFileDetails on WestpacTransactionImportFile {
    id
    shortRef
    fileName
    uploadedAt
    createdByUser {
      id
      ...UserProfileUI
    }
    reconciliation(maxDays: $maxDays) {
      id
      maxDays
      totalTransactionCount
      transactions {
        id
        ...AdminFileImportTransaction
      }
    }
  }
`);

const formSchema = z.array(
  z.object({
    transactionId: z.string(),
    optionId: z.string(),
  }),
);

type FormValues = z.input<typeof formSchema>;

export const Route = createFileRoute(
  "/__auth/transaction-import_/$fileImportId",
)({
  head: () => opsHead("Transaction Import"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  if (!params.fileImportId) {
    throw new NotFoundError();
  }

  const gqlData = useSuspenseQuery(
    graphql(`
      query AdminTransactionFileImportDetails($id: ID!, $maxDays: Int!) {
        node(id: $id) {
          id
          ... on MacquarieTransactionImportFile {
            ...AdminMacquarieFileImportFileDetails
          }
          ... on WestpacTransactionImportFile {
            ...AdminWestpacFileImportFileDetails
          }
        }
      }
    `),
    {
      variables: {
        id: params.fileImportId,
        maxDays,
      },
    },
  );
  const file = gqlData.data.node;
  if (
    !file ||
    (file.__typename !== "MacquarieTransactionImportFile" &&
      file.__typename !== "WestpacTransactionImportFile")
  ) {
    throw new NotFoundError();
  }

  useOpsTitle(
    `${prettyPrintImportFileType(file.__typename)} CSV file import "${file.fileName}"`,
  );

  const [createTransactions, { loading: createTransactionLoading, error }] =
    useMutation(
      graphql(`
        mutation TransactionImportCreateTransaction($input: CreateLedgerTransactionsInput!) {
          createLedgerTransactions(
            input: $input
          ) {
            transactions {
              id
            }
          }
        }
      `),
    );

  const transactions = file.reconciliation?.transactions ?? [];
  const newTransactions = transactions.filter((t) => t.importStatus === "NEW");
  const matchedTransactionsCount = transactions.filter(
    (t) =>
      t.importStatus === "NEW" &&
      t.options?.some(
        (v) =>
          (v.__typename === "FileImportTransactionPayoutOption" &&
            v.matchedWithReference) ||
          (v.__typename === "FileImportTransactionTransferOption" &&
            v.matchedWithReference),
      ),
  ).length;

  const form = useAppForm({
    defaultValues: transactions.map((t) => ({
      transactionId: t.id,
      optionId:
        t.options?.length && "id" in t.options[0] ? t.options[0].id : "",
    })) as FormValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        throw new Error(`parsing form ${parsed.error}`);
      }

      const transfers: CreateTransferInput[] = [];

      const ledgerTransactions: CreateLedgerTransactionInput[] =
        parsed.data.flatMap((d) => {
          const fileImportTransaction = transactions.find(
            (t) => t.id === d.transactionId,
          );
          if (!fileImportTransaction) {
            throw new Error(
              `expected file import transaction ${d.transactionId}`,
            );
          }
          if (fileImportTransaction.importStatus !== "NEW") {
            return [];
          }

          const direction = fileImportTransaction.amount.isPos()
            ? "CREDIT"
            : "DEBIT";

          const effectiveAt = DateTime.fromISO(fileImportTransaction.date, {
            zone: "Australia/Sydney",
          }).set({ minute: 0, hour: 17 });
          if (!effectiveAt.isValid) {
            throw new Error(
              `transaction date invalid ${fileImportTransaction.date}`,
            );
          }

          const unitId =
            fileImportTransaction.asset?.currency ??
            fileImportTransaction.custodyAccount?.currency;
          if (!unitId) {
            throw new Error("expected unitId to be defined");
          }

          const selectedOption = fileImportTransaction.options?.find(
            (o) => "id" in o && o.id === d.optionId,
          );
          if (!selectedOption) {
            throw new Error(`selected option not found for ${d.transactionId}`);
          }
          let assetId = fileImportTransaction.asset?.id;
          if (fileImportTransaction.custodyAccount) {
            switch (selectedOption.__typename) {
              case "FileImportTransactionTransferOption": {
                if (
                  selectedOption.transfer?.sourceAsset?.custodyAccount?.id ===
                  fileImportTransaction.custodyAccount.id
                ) {
                  assetId = selectedOption.transfer?.sourceAsset?.id;
                } else if (
                  selectedOption.transfer?.destinationAsset?.custodyAccount
                    ?.id === fileImportTransaction.custodyAccount.id
                ) {
                  assetId = selectedOption.transfer?.destinationAsset?.id;
                }
                break;
              }
              case "FileImportTransactionWestpacPaymentInstructionOption": {
                if (direction !== "DEBIT") {
                  throw new Error(
                    `expected westpac payment instruction debit for ${d.transactionId}`,
                  );
                }
                const paymentInstruction =
                  selectedOption.westpacPaymentInstruction;
                if (!paymentInstruction) {
                  throw new Error(
                    `expected payment instruction for ${d.transactionId}`,
                  );
                }
                if (!paymentInstruction.transfers?.length) {
                  throw new Error(
                    `expected payment instruction transfers for ${d.transactionId}`,
                  );
                }

                return paymentInstruction.transfers.map((transfer) => {
                  if (!transfer.sourceAsset) {
                    throw new Error(
                      `expected transfer source asset for ${transfer.id}`,
                    );
                  }
                  if (!transfer.currencyAmount) {
                    throw new Error(
                      `expected transfer currency amount for ${transfer.id}`,
                    );
                  }

                  return {
                    assetId: transfer.sourceAsset.id,
                    effectiveAt: effectiveAt.toJSDate(),
                    direction: "DEBIT",
                    unitId: transfer.currencyAmount.currency,
                    normalAmount: transfer.currencyAmount.amount,
                    description: fileImportTransaction.description,
                    reference: fileImportTransaction.description,
                    transactionType: "TRANSFER_OUT",
                    transferId: transfer.id,
                    payoutId: undefined,
                    depositId: undefined,
                  };
                });
              }
              case "FileImportTransactionBlackrockUnitOrderOption": {
                if (direction !== "CREDIT") {
                  throw new Error(
                    `expected westpac payment instruction credit for ${d.transactionId}`,
                  );
                }
                const blackrockUnitOrder = selectedOption.blackrockUnitOrder;
                if (!blackrockUnitOrder) {
                  throw new Error(
                    `expected blackrockUnitOrder for ${d.transactionId}`,
                  );
                }
                if (!blackrockUnitOrder.transfers?.length) {
                  throw new Error(
                    `expected blackrockUnitOrder transfers for ${d.transactionId}`,
                  );
                }

                return blackrockUnitOrder.transfers.map((transfer) => {
                  if (!transfer.destinationAsset) {
                    throw new Error(
                      `expected transfer destination asset for ${transfer.id}`,
                    );
                  }
                  if (!transfer.currencyAmount) {
                    throw new Error(
                      `expected transfer currency amount for ${transfer.id}`,
                    );
                  }

                  return {
                    assetId: transfer.destinationAsset.id,
                    effectiveAt: effectiveAt.toJSDate(),
                    direction: "CREDIT",
                    unitId: transfer.currencyAmount.currency,
                    normalAmount: transfer.currencyAmount.amount,
                    description: fileImportTransaction.description,
                    reference: fileImportTransaction.description,
                    transactionType: "TRANSFER_IN",
                    transferId: transfer.id,
                    payoutId: undefined,
                    depositId: undefined,
                  };
                });
              }
              default: {
                throw new Error(
                  `${selectedOption.__typename} unimplemented for custody`,
                );
              }
            }
          }
          if (!assetId) {
            throw new Error(
              `expected asset for file import transaction ${d.transactionId}`,
            );
          }

          const newTransactionInput: CreateLedgerTransactionInput = {
            assetId: assetId,
            effectiveAt: effectiveAt.toJSDate(),
            direction,
            unitId,
            normalAmount: fileImportTransaction.amount.abs(),
            description: fileImportTransaction.description,
            reference: fileImportTransaction.description,
            transactionType: "TRANSFER_IN",
            transferId: undefined,
            payoutId: undefined,
            depositId: undefined,
          };

          switch (selectedOption.__typename) {
            case "FileImportTransactionTransferOption":
              const transfer = selectedOption.transfer;
              if (!transfer) {
                throw new Error(
                  `expected transfer in option for ${d.transactionId}`,
                );
              }
              newTransactionInput.transferId = transfer.id;
              newTransactionInput.transactionType =
                direction === "CREDIT" ? "TRANSFER_IN" : "TRANSFER_OUT";
              break;
            case "FileImportTransactionPayoutOption":
              const payout = selectedOption.payout;
              if (!payout) {
                throw new Error(
                  `expected payout in option for ${d.transactionId}`,
                );
              }
              newTransactionInput.payoutId = payout.id;
              newTransactionInput.transactionType = "PAYOUT";
              break;
            case "FileImportTransactionAnnotationOption":
              newTransactionInput.transactionType = selectedOption.annotation;
              break;
            case "FileImportTransactionNewDepositOption":
              newTransactionInput.transactionType = "DEPOSIT";
              break;
            case "FileImportTransactionTransferNewOption":
              newTransactionInput.transactionType =
                direction === "CREDIT" ? "TRANSFER_IN" : "TRANSFER_OUT";
              newTransactionInput.transferId = selectedOption.transferId;

              if (direction === "DEBIT") {
                const sourceAsset = fileImportTransaction.asset;
                if (!sourceAsset) {
                  throw new Error(`expected asset for ${d.transactionId}`);
                }
                const destinationAsset = selectedOption.asset;
                if (!destinationAsset) {
                  throw new Error(
                    `expected asset for selected option ${d.transactionId}`,
                  );
                }
                const organisationId =
                  sourceAsset.businessEntity?.organisation?.id;
                if (!organisationId) {
                  throw new Error(
                    `expected organisation for ${d.transactionId}`,
                  );
                }

                transfers.push({
                  transferId: selectedOption.transferId,
                  amount: fileImportTransaction.amount.abs(),
                  unit: "AUD",
                  side: "SOURCE",
                  date: effectiveAt.toJSDate(),
                  description: fileImportTransaction.description,
                  sourceAssetId: sourceAsset.id,
                  destinationAssetId: destinationAsset.id,
                  organisationId,
                });
              }
              break;
            case "FileImportTransactionWestpacPaymentInstructionOption":
            case "FileImportTransactionBlackrockUnitOrderOption":
              throw new Error("unimplemented");
            default:
              throw neverFunc(selectedOption);
          }

          return [newTransactionInput];
        });

      await createTransactions({
        variables: {
          input: {
            ledgerTransactions,
            transfers,
            initiateBalanceSweep: true,
            fileImportId: params.fileImportId,
          },
        },
        onCompleted: () => {
          void navigate({ to: "/transactions-queue/ready-today" });
        },
        onError: (error) => {
          const errorMessage =
            error.graphQLErrors.map((e) => e.message).join(", ") ??
            "Server error";
          toast({
            kind: "error",
            title: "Error creating transactions",
            description: errorMessage,
          });
        },
      });
    },
  });

  const formValid = useStore(form.store, (state) => {
    const parsed = formSchema.safeParse(state.values);
    return parsed.success;
  });

  return (
    <div className="flex flex-col gap-4 p-12">
      <AdminPageHeader
        title={`${prettyPrintImportFileType(file.__typename)} CSV file import "${file.fileName}"`}
      />

      {error && (
        <Alert title="Import Error" kind="error" description={error.message} />
      )}

      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex w-full items-end justify-between">
              <div>
                <div className="flex space-x-1">
                  <div className="font-semibold">
                    {file.reconciliation?.totalTransactionCount}
                  </div>
                  <div>transactions found in CSV</div>
                </div>
                <div className="flex space-x-1">
                  <div className="font-semibold">{transactions.length}</div>
                  <div>transactions found in the last {maxDays} days</div>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="font-semibold">
                    {matchedTransactionsCount}
                  </div>
                  <div>transactions auto-reconciled by Primary AI</div>
                  <div>
                    <PrimarySparkLogo
                      className="h-4 w-4 shrink-0"
                      variant={
                        matchedTransactionsCount > 0 ? "default" : "secondary"
                      }
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={
                  createTransactionLoading ||
                  !newTransactions.length ||
                  !formValid
                }
                loading={createTransactionLoading}
              >
                Import {newTransactions.length} transactions
              </Button>
            </div>
            <DataTable
              data={transactions}
              columns={createColumns(form)}
              pageSize={100_000}
              hidePagination
              className="overflow-hidden border border-zinc-200"
              showCsvExport={false}
            />
          </div>
        </form>
      </form.AppForm>
    </div>
  );
}

const createColumns = (
  form: TanstackAppForm<FormValues>,
): ColumnDef<AdminFileImportTransactionFragment>[] => [
  {
    id: "date",
    accessorFn: (row) => row.date,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Transaction date" />
    ),
    cell: ({ row }) => {
      return <DateUI variant="DATE" date={new Date(row.original.date)} />;
    },
  },
  {
    id: "asset",
    accessorFn: (row) => row.asset,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset" />
    ),
    cell: ({ row }) => {
      if (!row.original.asset) {
        return "-";
      }
      const organisation = row.original.asset.businessEntity?.organisation;
      if (!organisation) {
        return "-";
      }
      return (
        <div className="flex w-fit items-center gap-2">
          {organisation?.squareLogoUrl && (
            <img
              className="size-8 rounded-xs"
              src={organisation.squareLogoUrl}
              alt="Organisation logo"
            />
          )}
          <div className="flex flex-col">
            {organisation?.name} - {row.original.asset.businessEntity?.name}
            <div>{row.original.asset.name}</div>
          </div>
        </div>
      );
    },
  },
  {
    id: "description",
    accessorFn: (row) => row.description,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    cell: ({ row }) => {
      return (
        <div>
          <div>{row.original.description}</div>
          <div className="font-secondary text-xs">
            Row {row.original.rowNumber + 1}
          </div>
        </div>
      );
    },
  },
  {
    id: "amount",
    accessorFn: (row) => row.amount,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex gap-2">
          {row.original.amount.isPos() ? "CR" : "DR"}
          <CurrencyAmountUI
            amount={row.original.amount.abs()}
            currency="AUD"
            hideCurrency
          />
        </div>
      );
    },
  },
  {
    id: "purpose",
    accessorFn: (row) => row.options,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Purpose"
        className="min-w-[500px]"
      />
    ),
    cell: ({ row }) => {
      switch (row.original.importStatus) {
        case "EXISTING":
          return (
            <div className="flex space-x-1">
              <Link
                to={`/ledger/transactions/${row.original.ledgerEntry?.transaction?.id}`}
                target="_blank"
                className="rounded-md border border-zinc-200 p-2"
              >
                <div className="flex items-center gap-3 pr-3">
                  <RiCheckboxCircleFill className="text-lg text-success-400" />
                  <div className="flex flex-col gap-1 text-neutral-400 text-sm">
                    Imported Ledger entry #{row.original.ledgerEntry?.shortRef}
                  </div>
                </div>
              </Link>
            </div>
          );
        case "UNKNOWN":
          return (
            <div className="flex space-x-1">
              <RiCheckboxBlankCircleFill className="text-error-400 text-lg" />
              <div>Failed to reconcile</div>
            </div>
          );
        case "NEW":
          const options = row.original.options;
          if (!options) {
            throw new Error(
              `expected transaction options for ${row.original.id}`,
            );
          }

          return (
            <form.AppField
              name={`[${row.index}].optionId` as `[${number}].optionId`}
            >
              {(field) => (
                <field.SelectField
                  placeholder="Select an option"
                  errorVisibilityMode="onBlur"
                >
                  {options.map((opt) => {
                    if (!("id" in opt)) {
                      throw new Error("expected option to have id");
                    }
                    return (
                      <field.SelectItem key={opt.id} value={opt.id}>
                        <div className="flex flex-grow">
                          {(() => {
                            switch (opt.__typename) {
                              case "FileImportTransactionNewDepositOption":
                                return (
                                  <div className="font-semibold">
                                    New unplanned deposit
                                  </div>
                                );
                              case "FileImportTransactionPayoutOption":
                                if (!opt.payout) {
                                  return null;
                                }
                                return (
                                  <div className="flex flex-grow flex-col">
                                    <div className="flex w-full justify-between">
                                      <div className="flex items-center gap-2 font-semibold">
                                        {opt.matchedWithReference && (
                                          <RiCheckboxCircleFill className="text-lg text-success-400" />
                                        )}
                                        #{opt.payout.shortRef} Payout
                                      </div>

                                      <div>
                                        {opt.payout.amount && (
                                          <CurrencyAmountUI
                                            amount={opt.payout.amount.amount}
                                            currency={
                                              opt.payout.amount.currency
                                            }
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex w-full justify-between">
                                      <div>
                                        <DateUI
                                          date={opt.payout.requestedForDate}
                                          variant="DATE"
                                        />
                                        {" · "}
                                        {
                                          opt.payout?.beneficiaryAccount
                                            ?.accountName
                                        }
                                      </div>

                                      {opt.matchedWithReference && (
                                        <Badge variant="outline">
                                          Rule
                                          <PrimarySparkLogo className="ml-1 inline-block h-3 w-3 align-middle opacity-80" />
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                );
                              case "FileImportTransactionTransferOption":
                                if (!opt.transfer) {
                                  return null;
                                }
                                return (
                                  <div className="flex flex-grow flex-col">
                                    <div className="flex w-full justify-between">
                                      <div className="flex items-center gap-2 font-semibold">
                                        {opt.matchedWithReference && (
                                          <RiCheckboxCircleFill className="text-lg text-success-400" />
                                        )}
                                        #{opt.transfer.shortRef} Transfer
                                      </div>

                                      {row.original.custodyAccount && (
                                        <div className="flex flex-row items-center gap-2">
                                          <Icon
                                            imageUrl={
                                              opt.transfer.sourceAsset
                                                ?.businessEntity?.organisation
                                                ?.squareLogoUrl ?? undefined
                                            }
                                          />
                                          <div className="flex flex-col overflow-hidden">
                                            <p className="max-w-32 truncate text-sm">
                                              {
                                                opt.transfer.sourceAsset
                                                  ?.businessEntity?.organisation
                                                  ?.name
                                              }
                                            </p>
                                            <p className="max-w-32 truncate text-secondary text-xs">
                                              {
                                                opt.transfer.sourceAsset
                                                  ?.businessEntity?.name
                                              }
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      <div>
                                        {opt.transfer.currencyAmount && (
                                          <CurrencyAmountUI
                                            amount={
                                              opt.transfer.currencyAmount.amount
                                            }
                                            currency={
                                              opt.transfer.currencyAmount
                                                .currency
                                            }
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex w-full justify-between">
                                      <DateUI
                                        date={opt.transfer.requestedForDate}
                                        variant="DATE"
                                      />
                                      {opt.matchedWithReference && (
                                        <Badge variant="outline">
                                          Rule
                                          <PrimarySparkLogo className="ml-1 inline-block h-3 w-3 align-middle opacity-80" />
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                );
                              case "FileImportTransactionAnnotationOption":
                                return (
                                  <div className="font-semibold">
                                    {prettyPrintEnum(opt.annotation)}
                                  </div>
                                );
                              case "FileImportTransactionTransferNewOption":
                                return (
                                  <div className="font-semibold">
                                    #{opt.transferId.slice(-6).toUpperCase()}{" "}
                                    New transfer to {opt.asset?.name}
                                  </div>
                                );
                              case "FileImportTransactionWestpacPaymentInstructionOption":
                                if (!opt.westpacPaymentInstruction) {
                                  return null;
                                }

                                return (
                                  <div className="flex flex-grow flex-col gap-2">
                                    <div className="flex w-full justify-between">
                                      <div className="font-semibold">
                                        #
                                        {opt.westpacPaymentInstruction.shortRef}{" "}
                                        Payment instruction
                                      </div>
                                      <div>
                                        <CurrencyAmountUI
                                          amount={
                                            opt.westpacPaymentInstruction
                                              .currencyAmount.amount
                                          }
                                          currency={
                                            opt.westpacPaymentInstruction
                                              .currencyAmount.currency
                                          }
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 p-2">
                                      <div className="flex min-w-0 items-center gap-2">
                                        <Icon
                                          imageUrl={
                                            opt.westpacPaymentInstruction
                                              .destinationCustodyAccount
                                              ?.product?.provider?.name
                                              ? brandLogosByKey[
                                                  opt.westpacPaymentInstruction
                                                    .destinationCustodyAccount
                                                    .product.provider?.name
                                                ]?.logoUrl
                                              : undefined
                                          }
                                        />
                                        <div className="flex min-w-0 flex-col">
                                          <div className="font-semibold">
                                            {
                                              opt.westpacPaymentInstruction
                                                .transfers?.length
                                            }{" "}
                                            linked transfer
                                            {opt.westpacPaymentInstruction
                                              .transfers?.length === 1
                                              ? ""
                                              : "s"}
                                          </div>
                                          <div className="truncate text-secondary text-xs">
                                            {opt.westpacPaymentInstruction
                                              .destinationCustodyAccount
                                              ?.shortRef ??
                                              "Destination custody account"}
                                            {" · "}
                                            {opt.westpacPaymentInstruction
                                              .destinationCustodyAccount
                                              ?.product?.internalName ??
                                              "Destination account"}
                                            {opt.westpacPaymentInstruction
                                              .transfers &&
                                            opt.westpacPaymentInstruction
                                              .transfers.length > 1
                                              ? " + more"
                                              : ""}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right text-secondary text-xs">
                                        Creates one transfer out transaction per
                                        linked transfer
                                      </div>
                                    </div>
                                  </div>
                                );
                              case "FileImportTransactionBlackrockUnitOrderOption":
                                if (!opt.blackrockUnitOrder) {
                                  return null;
                                }

                                return (
                                  <div className="flex flex-grow flex-col gap-2">
                                    <div className="flex w-full justify-between">
                                      <div className="font-semibold">
                                        #{opt.blackrockUnitOrder.shortRef}{" "}
                                        Blackrock Unit Order
                                      </div>
                                      <div>
                                        <CurrencyAmountUI
                                          {...opt.blackrockUnitOrder
                                            .currencyAmount}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 p-2">
                                      <div className="flex min-w-0 items-center gap-2">
                                        <Icon
                                          imageUrl={
                                            opt.blackrockUnitOrder
                                              .custodyAccount?.product?.provider
                                              ?.name
                                              ? brandLogosByKey[
                                                  opt.blackrockUnitOrder
                                                    .custodyAccount.product
                                                    .provider?.name
                                                ]?.logoUrl
                                              : undefined
                                          }
                                        />
                                        <div className="flex min-w-0 flex-col">
                                          <div className="font-semibold">
                                            {
                                              opt.blackrockUnitOrder.transfers
                                                ?.length
                                            }{" "}
                                            linked transfer
                                            {opt.blackrockUnitOrder.transfers
                                              ?.length === 1
                                              ? ""
                                              : "s"}
                                          </div>
                                          <div className="truncate text-secondary text-xs">
                                            {opt.blackrockUnitOrder
                                              .custodyAccount?.shortRef ??
                                              "Destination custody account"}
                                            {" · "}
                                            {opt.blackrockUnitOrder
                                              .custodyAccount?.product
                                              ?.internalName ??
                                              "Destination account"}
                                            {opt.blackrockUnitOrder.transfers &&
                                            opt.blackrockUnitOrder.transfers
                                              .length > 1
                                              ? " + more"
                                              : ""}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right text-secondary text-xs">
                                        Creates one transfer out transaction per
                                        linked transfer
                                      </div>
                                    </div>
                                  </div>
                                );
                              default:
                                throw neverFunc(opt);
                            }
                          })()}
                        </div>
                      </field.SelectItem>
                    );
                  })}
                </field.SelectField>
              )}
            </form.AppField>
          );
        default:
          throw new Error("expected import status");
      }
    },
  },
];

function prettyPrintImportFileType(
  fileType: "MacquarieTransactionImportFile" | "WestpacTransactionImportFile",
): string {
  switch (fileType) {
    case "MacquarieTransactionImportFile":
      return "Macquarie";
    case "WestpacTransactionImportFile":
      return "Westpac";
    default:
      return neverFunc(fileType);
  }
}
